import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ImprovedNavbar from './components/layout/ImprovedNavbar';
import LoadingSpinner from './components/common/LoadingSpinner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { QueryProvider } from './contexts/QueryProvider';
import { RoomsProvider } from './contexts/RoomsContext';
import { CartProvider } from './contexts/CartContext';

// Lazy load components for code splitting
const Login = React.lazy(() => import('./components/auth/Login'));
const Signup = React.lazy(() => import('./components/auth/Signup'));
const Settings = React.lazy(() => import('./components/auth/Settings'));
const CalendarPage = React.lazy(() => import('./components/calendar/CalendarPage'));
const CalendarHome = React.lazy(() => import('./components/calendar/CalendarHome'));
const InventoryPage = React.lazy(() => import('./components/inventory/InventoryPage'));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <LoadingSpinner size="lg" text="Loading page..." />
  </div>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen dark:bg-gray-900">
        <div className="loader" />
      </div>
    );
  }
  
  return currentUser ? <>{children}</> : <Navigate to="/login" />;
};

const RoleBasedHome = () => {
  const { currentUser } = useAuth();

  if (!currentUser) return null;

  // All authenticated users see the home menu
  return <CalendarHome />;
};

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
        <ImprovedNavbar />
        <main>
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />

              {/* Protected routes */}
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <Settings />
                  </PrivateRoute>
                }
              />

              <Route
                path="/inventory"
                element={
                  <PrivateRoute>
                    <InventoryPage />
                  </PrivateRoute>
                }
              />

              <Route
                path="/calendar"
                element={
                  <PrivateRoute>
                    <CalendarPage />
                  </PrivateRoute>
                }
              />

              {/* Home route - redirects based on role */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <RoleBasedHome />
                  </PrivateRoute>
                }
              />

              {/* Catch all route - redirects to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </Router>
  );
}

const AppWrapper = () => {
  return (
    <QueryProvider>
      <AuthProvider>
        <ThemeProvider>
          <RoomsProvider>
            <CartProvider>
              <App />
            </CartProvider>
          </RoomsProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryProvider>
  );
};

export default AppWrapper;